import React, { useEffect, useState, useRef, useContext } from 'react';
import * as FormActions from '../../actions/actionCreators';
import errorMessage from '../../utils/errorMessage';
import TooltipHelper from './TooltipHelper';
import TextFieldHelperText from './TextFieldHelperText';
import { useAppDispatch, useAppSelector } from '../../hooks';
import type { RootState } from '../../store';
import { FieldsetValidatorContext } from './Form';

interface Document {
  file: File;
  error: string | null;
}

interface UploadFieldProps {
  name: string;
  label: string;
  required?: boolean;
  form: string;
  value?: string;
  type?: string;
  error?: string;
  instruction?: string;
  hideOptional?: boolean;
  title?: string;
}

const genericMessageLimit = 'Maximum of 5 files';

const UploadField: React.FC<UploadFieldProps> = ({
  name,
  label,
  required,
  form,
  type,
  instruction,
  hideOptional,
  title,
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const componentRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const fieldsetValidator = useContext(FieldsetValidatorContext);

  const documents = useAppSelector(
    (state: RootState) => state.forms[form][name].value
  ) as Document[];

  const validate = () => {
    const validationErrors: string[] = [];

    if (documents && documents.length > 5) {
      validationErrors.push(genericMessageLimit);
    }

    const errorMessages = errorMessage(
      { required, name, label },
      documents.length < 6
    );

    if (errorMessages) {
      validationErrors.push(errorMessages);
    }

    setErrors((prevErrors) => {
      const fileErrors = prevErrors.filter(
        (err) => !err.includes(genericMessageLimit) && err !== errorMessages
      );

      return [...fileErrors, ...validationErrors];
    });

    return validationErrors.length === 0;
  };

  useEffect(() => {
    const valid = validate();

    if (componentRef.current) {
      fieldsetValidator.register({
        el: componentRef.current,
        name,
        required,
        valid,
      });
    }

    return () => fieldsetValidator.unregister(componentRef.current);
  }, [fieldsetValidator, componentRef]);

  const handleAddDocument = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    // reset errors on file upload
    setErrors([]);

    const files = Array.from(e.target.files || []);

    const totalFiles = files.length + documents.length;
    if (totalFiles > 5) {
      setErrors([genericMessageLimit]);
      return;
    }

    const maxSize = 5242880;
    const acceptedFormats = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'doc',
      'docx',
      'pdf',
      'odt',
      'odp',
      'ods',
      'jpg',
      'jpeg',
      'png',
      'image/png',
      'xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'csv',
      'text/csv',
    ];
    const newErrors: string[] = [];

    const fileError = {
      fileSizeError: 'is too large',
      fileFormatError: 'is not an accepted file format',
    };

    const storeDocuments = documents ? [...documents] : [];

    if (!files || files.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const { type, size, name } = files[i];
      const format = type || name.split('.').pop();

      // Only add files that pass all validations
      if (
        size <= maxSize &&
        acceptedFormats.includes(format) &&
        documents.length + storeDocuments.length - documents.length < 5
      ) {
        storeDocuments.push({
          file: files[i],
          error:
            size > maxSize
              ? fileError.fileSizeError
              : !acceptedFormats.includes(type)
              ? fileError.fileFormatError
              : null,
        });
      }

      if (size > maxSize) {
        newErrors.push(`${name} ${fileError.fileSizeError}`);
      }

      if (!acceptedFormats.includes(format)) {
        newErrors.push(`${name} ${fileError.fileFormatError}`);
      }

      if (i > 5 || documents.length >= 5) {
        if (!newErrors.includes(genericMessageLimit)) {
          newErrors.push(genericMessageLimit);
        }
      }
    }

    dispatch(FormActions.addDocument(form, name, storeDocuments));

    // Add a final check for too many documents
    if (storeDocuments.length > 5 && !newErrors.includes(genericMessageLimit)) {
      newErrors.push(genericMessageLimit);
    }

    e.target.value = '';

    // Add new file-specific errors to existing errors
    if (newErrors.length > 0) {
      setErrors((prevErrors) => {
        // Filter out duplicates when adding new errors
        const combinedErrors = [...prevErrors];

        newErrors.forEach((newError) => {
          if (!combinedErrors.includes(newError)) {
            combinedErrors.push(newError);
          }
        });

        return combinedErrors;
      });
    }

    // Run validation to update validation-related errors
    validate();
  };

  const handleRemoveDocument = (index: number, e: React.MouseEvent) => {
    e.preventDefault();

    // Get the filename before removing to clear related errors
    const filename = documents[index]?.file?.name;

    dispatch(FormActions.removeDocument(form, index));

    // Remove any errors related to this specific file
    if (filename) {
      setErrors((prevErrors) => {
        return prevErrors.filter((error) => !error.includes(filename));
      });
    }

    // Run validation to update validation-related errors
    validate();
  };

  const renderUploadedFiles = () => {
    if (!documents) {
      return null;
    }

    return documents.map((document, index) => (
      <React.Fragment key={index}>
        <div className="step__upload">
          <span className="step__file">
            <img
              className="step__fileicon"
              src={'/_resources/themes/bos/images/file-tick.png'}
              alt="File icon"
            />
            <span className="step__filename">{document.file.name}</span>
          </span>

          <button
            className={'step__remove'}
            type="button"
            aria-label="Remove file"
            onClick={(e) => handleRemoveDocument(index, e)}
          >
            <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg">
              <g
                stroke="#333"
                strokeWidth="2.56"
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
              >
                <path d="M1.333 1.333l7.008 7.008M8.667 1.333L1.659 8.341" />
              </g>
            </svg>
          </button>
        </div>
      </React.Fragment>
    ));
  };

  const acceptedFormat =
    'Accepted file formats (.pdf, .doc, .docx, .odt, .ods, .odp, .jpg, .xls, .csv, .png)';

  return (
    <div ref={componentRef}>
      {!hideOptional && (
        <h3 className="step__heading mt--0">
          {title && title} (optional) <TooltipHelper content={acceptedFormat} />
        </h3>
      )}
      <p className="mt--0 mb--1">
        {instruction ||
          'Please attach a maximum of 5 files relating to your complaint (each attachment cannot exceed 5mb)'}
      </p>

      <br />

      {renderUploadedFiles()}

      <div className="form__uploadfieldholder">
        <label className="button button--upload mt--2" htmlFor={name}>
          <svg
            className="mr--1"
            width="14"
            height="18"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.283 6.19a.552.552 0 0 0 0 .817c.232.22.62.22.851 0l4.361-4.14V16.53c0 .318.267.571.603.571.335 0 .61-.253.61-.571V2.866l4.354 4.141c.24.22.628.22.86 0a.552.552 0 0 0 0-.817L7.528 1.07a.614.614 0 0 0-.851 0L1.283 6.19z"
              fill="currentColor"
              fillRule="nonzero"
              stroke="currentColor"
            />
          </svg>
          {label}
        </label>
        <input
          type={type}
          name={name}
          id={name}
          onBlur={validate}
          onChange={handleAddDocument}
          required={required}
          multiple
          hidden
        />
        {documents.length > 5 && errors.length > 0 && (
          <div className="validation-icon validation-wrong"></div>
        )}
        {errors && errors.length > 0 ? (
          <div className="uploadfield__errors">
            {errors.map((error, index) => (
              <TextFieldHelperText
                persistent
                validationMsg
                style={{ color: 'red' }}
                key={index}
              >
                {error}
              </TextFieldHelperText>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UploadField;
